import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, LineChart, Line, ComposedChart } from 'recharts';
import { Wind, CloudRain, Thermometer, Droplets, TrendingUp, TrendingDown, RefreshCw, Calendar, MapPin, ArrowRight, Waves, CloudSnow } from 'lucide-react';
// --- Mock Data ---
const weatherData = Array.from({
  length: 24
}, (_, i) => {
  const hour = i;
  const windSpeed = 8 + Math.sin(hour / 3) * 4 + Math.random() * 2;
  const precip = Math.max(0, Math.sin(hour / 6) * 5);
  const temp = 2 + Math.sin((hour - 6) / 12 * Math.PI) * 5;
  return {
    time: `${hour}:00`,
    windSpeed: parseFloat(windSpeed.toFixed(1)),
    precipitation: parseFloat(precip.toFixed(1)),
    temperature: parseFloat(temp.toFixed(1)),
    hydroOutput: 45 + Math.random() * 2
  };
});
const hydroReservoirs = [{
  region: 'NO1 (Oslo)',
  level: 82,
  capacity: 100,
  trend: 'up'
}, {
  region: 'NO2 (Kristiansand)',
  level: 76,
  capacity: 100,
  trend: 'stable'
}, {
  region: 'SE1 (Luleå)',
  level: 88,
  capacity: 100,
  trend: 'up'
}, {
  region: 'SE3 (Stockholm)',
  level: 65,
  capacity: 100,
  trend: 'down'
}, {
  region: 'FI (Finland)',
  level: 72,
  capacity: 100,
  trend: 'stable'
}];
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-xl p-4 shadow-xl z-50 text-white" data-id="element-2199">
        <p className="text-xs font-bold text-slate-300 mb-2" data-id="element-2200">{label}</p>
        <div className="space-y-1" data-id="element-2201">
          {payload.map((entry: any) => <div key={entry.name} className="flex items-center justify-between gap-4" data-id="element-2202">
              <div className="flex items-center gap-2" data-id="element-2203">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: entry.color
            }} data-id="element-2204" />
                <span className="text-xs text-slate-300 capitalize" data-id="element-2205">
                  {entry.name}
                </span>
              </div>
              <span className="text-xs font-mono font-medium text-white" data-id="element-2206">
                {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                {entry.unit || ''}
              </span>
            </div>)}
        </div>
      </div>;
  }
  return null;
};
export function NordicsWeatherDashboard() {
  const [activeRegion, setActiveRegion] = useState('NO1');
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-slate-50/50" data-id="element-2207">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2208">
        <div data-id="element-2209">
          <div className="flex items-center gap-2 mb-1" data-id="element-2210">
            <div className="p-2 bg-cyan-600 rounded-lg" data-id="element-2211">
              <CloudSnow size={20} className="text-white" data-id="element-2212" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-2213">
              Nordics Weather & Hydro
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-2214">
            Wind forecasts, hydro reservoir levels, and precipitation analysis
          </p>
        </div>

        <div className="flex items-center gap-3 ml-11 md:ml-0" data-id="element-2215">
          <button className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm" data-id="element-2216">
            <Calendar size={16} data-id="element-2217" />
            <span data-id="element-2218">Next 24 Hours</span>
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition-colors shadow-sm shadow-cyan-500/20" data-id="element-2219">
            <RefreshCw size={16} data-id="element-2220" />
            <span data-id="element-2221">Update Models</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2222">
        {[{
        title: 'Avg Wind Speed',
        value: '12.4',
        unit: 'm/s',
        change: '+15%',
        trend: 'up',
        icon: Wind,
        color: 'cyan'
      }, {
        title: 'Hydro Reserves',
        value: '78.5',
        unit: '%',
        change: '+2.1%',
        trend: 'up',
        icon: Droplets,
        color: 'blue'
      }, {
        title: 'Precipitation',
        value: '14.2',
        unit: 'mm',
        change: '-5.4%',
        trend: 'down',
        icon: CloudRain,
        color: 'indigo'
      }, {
        title: 'Temperature',
        value: '4.2',
        unit: '°C',
        change: '-1.2°',
        trend: 'down',
        icon: Thermometer,
        color: 'teal'
      }].map((metric, idx) => <motion.div key={metric.title} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-5 shadow-sm hover:shadow-md transition-all group" data-id="element-2223">
            <div className="flex justify-between items-start mb-4" data-id="element-2224">
              <div className={`p-2 rounded-lg bg-${metric.color}-50 text-${metric.color}-600 group-hover:bg-${metric.color}-100 transition-colors`} data-id="element-2225">
                <metric.icon size={20} data-id="element-2226" />
              </div>
              <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${metric.trend === 'up' ? 'bg-cyan-50 text-cyan-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-2227">
                {metric.trend === 'up' ? <TrendingUp size={12} data-id="element-2228" /> : <TrendingDown size={12} data-id="element-2229" />}
                {metric.change}
              </div>
            </div>
            <div data-id="element-2230">
              <p className="text-sm text-slate-500 font-medium" data-id="element-2231">
                {metric.title}
              </p>
              <div className="flex items-baseline gap-1 mt-1" data-id="element-2232">
                <span className="text-2xl font-bold text-slate-900" data-id="element-2233">
                  {metric.value}
                </span>
                <span className="text-sm text-slate-400 font-medium" data-id="element-2234">
                  {metric.unit}
                </span>
              </div>
            </div>
          </motion.div>)}
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2235">
        {/* Wind Forecast */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-2 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 shadow-lg text-white" data-id="element-2236">
          <div className="flex items-center justify-between mb-6" data-id="element-2237">
            <div data-id="element-2238">
              <h3 className="text-lg font-bold text-white" data-id="element-2239">
                Wind Speed Forecast
              </h3>
              <p className="text-sm text-slate-400" data-id="element-2240">
                High-resolution wind model (ECMWF)
              </p>
            </div>
            <div className="flex gap-2" data-id="element-2241">
              <span className="px-2 py-1 bg-white/10 rounded text-xs font-medium text-cyan-300 border border-white/10" data-id="element-2242">
                Strong Winds Expected
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-2243">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2244">
              <AreaChart data={weatherData} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-2245">
                <defs data-id="element-2246">
                  <linearGradient id="colorWind" x1="0" y1="0" x2="0" y2="1" data-id="element-2247">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} data-id="element-2248" />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} data-id="element-2249" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" data-id="element-2250" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94a3b8'
              }} dy={10} data-id="element-2251" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94a3b8'
              }} label={{
                value: 'Wind Speed (m/s)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fill: '#94a3b8',
                  fontSize: 12
                }
              }} data-id="element-2252" />
                <Tooltip content={<CustomTooltip data-id="element-2254" />} data-id="element-2253" />
                <Area type="monotone" dataKey="windSpeed" name="Wind Speed" stroke="#06b6d4" fill="url(#colorWind)" strokeWidth={3} data-id="element-2255" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Hydro Reservoir Levels */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm flex flex-col" data-id="element-2256">
          <div className="mb-6" data-id="element-2257">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-2258">
              Hydro Reservoirs
            </h3>
            <p className="text-sm text-slate-500" data-id="element-2259">Filling levels by region</p>
          </div>

          <div className="flex-1 space-y-5" data-id="element-2260">
            {hydroReservoirs.map(res => <div key={res.region} data-id="element-2261">
                <div className="flex justify-between text-sm mb-2" data-id="element-2262">
                  <span className="font-medium text-slate-700" data-id="element-2263">
                    {res.region}
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-2264">{res.level}%</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-2265">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${res.level}%`
              }} transition={{
                duration: 1,
                ease: 'easeOut'
              }} className={`h-full rounded-full ${res.level > 80 ? 'bg-blue-500' : res.level > 60 ? 'bg-cyan-500' : 'bg-amber-500'}`} data-id="element-2266" />
                </div>
                <div className="flex justify-between mt-1" data-id="element-2267">
                  <span className="text-[10px] text-slate-400" data-id="element-2268">
                    Capacity: {res.capacity} GWh
                  </span>
                  <span className={`text-[10px] font-medium ${res.trend === 'up' ? 'text-emerald-600' : res.trend === 'down' ? 'text-rose-600' : 'text-slate-500'}`} data-id="element-2269">
                    {res.trend === 'up' ? 'Filling' : res.trend === 'down' ? 'Draining' : 'Stable'}
                  </span>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2270">
        {/* Precipitation & Temperature */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2271">
          <div className="mb-6 flex justify-between items-center" data-id="element-2272">
            <div data-id="element-2273">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-2274">
                Precipitation & Temp
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2275">Inflow forecast analysis</p>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium" data-id="element-2276">
              <span className="flex items-center gap-1 text-indigo-600" data-id="element-2277">
                <div className="w-2 h-2 rounded-full bg-indigo-500" data-id="element-2278" /> Rain (mm)
              </span>
              <span className="flex items-center gap-1 text-teal-600" data-id="element-2279">
                <div className="w-2 h-2 rounded-full bg-teal-500" data-id="element-2280" /> Temp (°C)
              </span>
            </div>
          </div>

          <div className="h-[250px] w-full" data-id="element-2281">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2282">
              <ComposedChart data={weatherData} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-2283">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-2284" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} interval={3} data-id="element-2285" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-2286" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-2287" />
                <Tooltip content={<CustomTooltip data-id="element-2289" />} data-id="element-2288" />
                <Bar yAxisId="left" dataKey="precipitation" name="Precipitation" fill="#6366f1" radius={[2, 2, 0, 0]} barSize={8} data-id="element-2290" />
                <Line yAxisId="right" type="monotone" dataKey="temperature" name="Temperature" stroke="#14b8a6" strokeWidth={2} dot={false} data-id="element-2291" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Regional Map Placeholder (Visual only) */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="bg-gradient-to-br from-cyan-900 to-blue-900 rounded-xl p-6 shadow-sm relative overflow-hidden" data-id="element-2292">
          <div className="absolute inset-0 opacity-20" data-id="element-2293">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-2294">
              <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" data-id="element-2295" />
            </svg>
          </div>

          <div className="relative z-10 h-full flex flex-col justify-between" data-id="element-2296">
            <div data-id="element-2297">
              <h3 className="text-lg font-bold text-white" data-id="element-2298">
                Regional Overview
              </h3>
              <p className="text-sm text-cyan-200" data-id="element-2299">Active weather warnings</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-2300">
              {[{
              region: 'Norway Coast',
              status: 'Storm Warning',
              color: 'rose'
            }, {
              region: 'Sweden Inland',
              status: 'Heavy Snow',
              color: 'cyan'
            }, {
              region: 'Finland South',
              status: 'Clear',
              color: 'emerald'
            }, {
              region: 'Denmark',
              status: 'High Winds',
              color: 'amber'
            }].map(item => <div key={item.region} className="bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/10" data-id="element-2301">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2302">
                    <MapPin size={14} className="text-cyan-300" data-id="element-2303" />
                    <span className="text-xs font-bold text-white" data-id="element-2304">
                      {item.region}
                    </span>
                  </div>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded full bg-${item.color}-500/20 text-${item.color}-200 border border-${item.color}-500/30`} data-id="element-2305">
                    {item.status}
                  </span>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}